// apps/client/src/hooks/loanBookViewFetchers.ts
//
// The transport half of the scoped loan-book panel. Deliberately SEPARATE from
// useLoanBookView.ts: importing `../lib/firebase` initialises a real Firebase app
// at module load, which makes any module that touches it unimportable from a
// hermetic unit test. The hook is the state machine and stays pure; these two
// functions are the only place a callable is named.
//
// The contract is produced by `getBrokerReport` and `getSharedBrokerReport`
// themselves rather than by a dedicated callable, so the panel can never disagree
// with the report printed beside it. The first view therefore arrives WITH the
// report (handed to the hook as `initialView`) and only a scope change pays for a
// round trip.
//
// FAIL-SAFE, LOAD-BEARING: a response with no `loanBookView` resolves to `null`,
// and a `null` view makes the report fall back to its existing single "Loan Book
// Value" card. No figure is ever invented.

import { httpsCallable } from 'firebase/functions';
import { functions } from '../lib/firebase';
import type { LoanBookMode, LoanBookView } from '../types/loanBookView';
import type { LoanBookViewFetcher } from './useLoanBookView';

/**
 * Authenticated Broker Performance Report.
 *
 * Re-requests the report for the newly selected scope and takes its
 * `loanBookView`. A response with no `loanBookView` means the tenant flag is off,
 * or the backend declined to build one. Both map to `null` = show the legacy card.
 */
export const brokerReportFetcher: LoanBookViewFetcher = async (req) => {
  const fn = httpsCallable<
    { companyId: string; brokerDisplay: string; loanBookMode: LoanBookMode },
    { loanBookView?: LoanBookView }
  >(functions, 'getBrokerReport');
  const res = await fn({
    companyId: req.companyId,
    brokerDisplay: req.brokerDisplay,
    loanBookMode: req.mode,
  });
  return res.data?.loanBookView ?? null;
};

/** Public shared report. The share token is the only credential it has. */
export const sharedBrokerReportFetcher: LoanBookViewFetcher = async (req) => {
  if (!req.shareToken) return null;
  const fn = httpsCallable<
    { token: string; loanBookMode: LoanBookMode },
    { loanBookView?: LoanBookView }
  >(functions, 'getSharedBrokerReport');
  const res = await fn({ token: req.shareToken, loanBookMode: req.mode });
  return res.data?.loanBookView ?? null;
};
